import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';
import { SessionService } from '../tenant/session.service';
import { SESION_CERRADA, SesionVencidaService } from '../tenant/sesion-vencida.service';
import { leerError, motivoDelError } from '../ui/motivo-del-error';

/**
 * Un solo aviso de error a la vez. SweetAlert no apila: el segundo tapa al primero, y la cajera los va cerrando uno
 * tras otro. Medido el 19/09 (mayorista en staging, prueba 6): recargar el historial sin internet abría dos, encimados.
 */
let avisoAbierto = false;

/**
 * Una petición que se hace sola en segundo plano y cuyo 403 no se le dice a quien está en la caja. Es el reintento de
 * la cola de consumo propio (Z168): un 403 ahí (SOLO_ADMINISTRADOR, DUENO_SOLO_ADMIN) deja el consumo esperando a
 * quien pueda mandarlo, y sin esto abría «No se pudo» en cada arranque y en cada vuelta de la red. Va por petición y no
 * por URL: el mismo POST hecho a mano (`registrar()`) sí avisa. El 401 sigue su camino (cierra la sesión).
 */
export const SIN_AVISO_EN_403 = new HttpContextToken<boolean>(() => false);

export function errorInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    // Se resuelven aquí (contexto de inyección del interceptor), no dentro del
    // catchError, donde ya no hay injector activo.
    const session = inject(SessionService);
    // Un solo aviso aunque venzan varias llamadas a la vez, y después del «Cobrado» si se estaba cobrando.
    const sesionVencida = inject(SesionVencidaService);

    // El agente de hardware (impresora/cajón, agentBaseUrl) es best-effort: corre en
    // la máquina del cliente y puede no estar disponible. Sus errores NO deben mostrar
    // modales — si no, el sondeo de estado de impresora satura la pantalla de alertas.
    const isAgentCall = req.url.startsWith(environment.agentBaseUrl);

    /**
     * Llamadas "best-effort" (N2): se consultan solas, en segundo plano, y su
     * fallo NO debe abrir un modal encima del cajero. Antes, un 500 en la
     * disponibilidad de rastreadores llenaba la pantalla de "Algo salió mal"
     * mientras se intentaba vender. El componente ya degrada por su cuenta.
     */
    const esBestEffort =
        req.url.includes('/orders/pager-availability') ||
        req.url.includes('/account/pagers') ||
        req.url.includes('/api/menu/sync') ||
        // F2b: «¿Quién eres?» pinta su 401/429 en la pantalla del PIN; el 409 (celular no compartido) y el 403 dejan el
        // flujo de hoy sin decir nada. Un «No se pudo» encima tapaba el campo del PIN.
        req.url.includes('/account/bodegueros-de-mi-bodega') ||
        req.url.includes('/account/pin-de-bodega') ||
        // El registro rápido desde la caja pinta sus errores bajo cada campo
        // (PIN incorrecto, ya existe, sin red): un modal encima del formulario
        // taparía el campo que hay que corregir (2026-09-11).
        req.url.includes('/api/menu/products/registro-rapido') ||
        // Multisede, fase 1: el modal de emparejar la caja pinta el 409 y el 404 dentro de sí, con el texto del
        // servidor. Un «No se pudo» global lo taparía (Swal es uno solo en pantalla).
        req.url.includes('/account/terminales/emparejar') ||
        // La bienvenida del alta pinta sus errores en la página (enlace vencido, casillas, clave, términos nuevos).
        req.url.includes('/auth/bienvenida/') ||
        // B9.5: las listas de la caja menor (categorías y proveedores, de core). Si no cargan, el diálogo las esconde y el
        // gasto se registra como siempre, sin aviso (Producto, TEXTOS 448ae00).
        req.url.startsWith(`${environment.coreApiBaseUrl}/expense-categories`) ||
        req.url.startsWith(`${environment.coreApiBaseUrl}/api/suppliers`) ||
        // D2 (05/11): la pregunta «¿se ofrece el consumo propio?». Con el interruptor de -mt apagado da 409
        // CONSUMO_PROPIO_APAGADO y salía «No se pudo» al entrar a la caja; la respuesta es «no se ofrece», no un error.
        // Registrar a mano (POST /api/consumo-propio) sigue avisando.
        req.url.endsWith('/api/consumo-propio/personas') ||
        // La creación de órdenes la maneja la capa offline, que muestra su
        // propio mensaje con el motivo real. Si además modalara el interceptor,
        // al reconectar salían N modales seguidos (uno por orden reintentada).
        req.url.includes('/orders/create');

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (isAgentCall) {
                console.warn('[agente hardware] no disponible:', req.url, error.status);
                return throwError(() => error);
            }

            // Multisede, fase 4: el usuario es de otra sede. Lo pinta la caja como bloqueo de arranque, con el texto del
            // servidor y «Cerrar sesión»; un «No se pudo» encima lo taparía. SOLO ese código: otro 403 sigue como hoy.
            if (error.status === 403 && leerError(error).codigo === 'USUARIO_DE_OTRA_SEDE') {
                return throwError(() => error);
            }

            // W1 (DrDev002): con el token del bodeguero, -mt da 403 ROL_SIN_ACCESO a todo lo que no es /auth, /account/sites ni
            // /api/bodega. Lo que el POS pide al arrancar (perfil, ajustes, catálogo…) cae ahí: ni aviso encima del Modo Bodega
            // ni sesión cerrada (eso es solo el 401). Queda en la consola, con la ruta.
            // Z50 (guía 1.2): y cualquier otro 403 con la sesión del bodeguero. Al entrar, la barra lateral pedía
            // /account/users y salía «Tu rol no puede ver esta lista» encima de /bodega. Sus pantallas pintan sus propios errores.
            if (error.status === 403 && (leerError(error).codigo === 'ROL_SIN_ACCESO' || session.session()?.role === 'bodeguero')) {
                console.warn('[bodega] 403 ROL_SIN_ACCESO, sin aviso:', req.url);
                return throwError(() => error);
            }

            if (error.status === 403 && req.context.get(SIN_AVISO_EN_403)) {
                console.warn('[segundo plano] 403 sin aviso:', req.url, leerError(error).codigo);
                return throwError(() => error);
            }

            // M33 (Líder, 14/10): el 403 de entrar (ALTA_PENDIENTE, PRUEBA_VENCIDA, NEGOCIO_SUSPENDIDO) lo pinta el login
            // en línea con el texto del servidor; un «No se pudo» encima lo repetía. Los demás 403 siguen igual.
            if (error.status === 403 && req.url === `${environment.apiBaseUrl}/auth/login`) {
                return throwError(() => error);
            }

            if (esBestEffort) {
                console.warn('[best-effort] falló sin interrumpir al cajero:', req.url, error.status);
                return throwError(() => error);
            }

            // TOKEN VENCIDO (N2/6.4): típico al volver a una pestaña suspendida.
            // Antes las llamadas fallaban en silencio (o con un "Algo salió mal"
            // que no llevaba a ningún lado) y el cajero seguía "adentro" sin
            // poder hacer nada. Ahora se cierra la sesión y se pide login.
            //
            // Con `codigo` (ECM, 23/09): un 401 puede ser un rechazo de NEGOCIO (p.ej. «Cambiar
            // contraseña» con la actual mal escrita, -mt AuthService.java:296) y no una sesión
            // vencida. El interceptor no puede leer el texto para distinguirlos (cambia con el
            // idioma o una tilde), así que la regla es el `codigo`: sin él, sesión vencida (lo de
            // siempre, el lado seguro); con él, no se toca la sesión y quien llamó lo muestra.
            if (error.status === 401 && !req.url.includes('/auth/')) {
                const codigo = leerError(error).codigo;
                // Cierre de sesiones por seguridad (DrDev002): el token ya está muerto en los tres servicios. Se cierra la
                // sesión con su aviso propio, no «Tu sesión expiró». Va ANTES de la regla del codigo: si no, dejaría a la
                // cajera «dentro» con todo fallando en silencio.
                if (codigo === SESION_CERRADA) {
                    if (session.tieneToken()) sesionVencida.cerrar('bloqueada');
                    return throwError(() => error);
                }
                if (codigo) {
                    return throwError(() => error);
                }
                if (session.tieneToken()) {
                    sesionVencida.cerrar();
                }
                return throwError(() => error);
            }

            // SIN RED: NO se muestra modal. El aviso lo da el banner flotante (N2/6.2). Antes, cobrar sin internet
            // abría un «Algo salió mal» que parecía que la venta se había perdido, y estaba guardada en el outbox.
            //
            // Dos formas de no tener red, y las dos cuentan:
            //  - `status 0`: la petición no salió;
            //  - el navegador SE SABE sin conexión: con el service worker de la app instalado, lo que no está en caché
            //    no da 0 sino una respuesta de error suya (504). Así salían dos modales al recargar sin internet.
            if (error.status === 0 || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
                console.warn('[sin conexión] no se pudo hablar con el servidor:', req.url, error.status);
                return throwError(() => error);
            }

            // El motivo real, con un solo lector para POS y panel (agente C,
            // 2026-09-08): `mensaje`/`message` del cuerpo; si el cuerpo es el
            // «Bad Request» pelado de Spring o un 500 sin texto, se dice lo que
            // se sabe por el status. Nunca «Algo salió mal» teniendo el motivo.
            let errorMessage = motivoDelError(error);

            if (error.status === 400 || error.status === 409) {
                if (errorMessage.includes('PagerOcupadoException') || errorMessage.toLowerCase().includes('ocupado')) {
                    errorMessage = 'El localizador (pager) seleccionado ya está ocupado.';
                } else if (errorMessage.includes('OrderEditNotAllowedException') || errorMessage.toLowerCase().includes('minutos')) {
                    errorMessage = 'No se puede editar la orden después de 7 minutos.';
                }
            }

            // Un solo aviso a la vez: varias llamadas que fallan juntas (al recargar, al volver la red) apilaban uno
            // por cada una. El motivo de las siguientes queda en la consola, que es donde se busca.
            if (avisoAbierto) {
                console.warn('[error] ya hay un aviso a la vista; este no se apila:', req.url, error.status, errorMessage);
                return throwError(() => error);
            }
            avisoAbierto = true;
            void Swal.fire({
                title: 'No se pudo',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'Entendido',
                customClass: {
                    popup: 'swal2-custom-popup',
                    title: 'swal2-custom-title',
                    confirmButton: 'swal2-custom-confirm-btn'
                },
                buttonsStyling: false
            }).finally(() => {
                avisoAbierto = false;
            });

            return throwError(() => error);
        })
    );
}
